// MACHINERY V2 (Stroje) — "Pridať z katalógu", the reduced add form (M2.8).
//
// Spec: data package §11 prefill table. Reference impl:
// design_handoff_machinery_v3/machinery-catalog.jsx (`AddDialog`); artboard
// 0903. Logic half = useCatalogAddForm.ts (300-line cap).
//
// >>> THIS REPLACES THE LAST MUI ADD MOUNT. <<<
// Until now a catalog model picked in the v2 flow opened v1's `MachineDialog`
// in its `catalog-search` mode through `preSelectedCatalogModel`
// (MachineDialog.tsx:487-492), mounted from fleet/FleetDialogs.tsx. M2.7's
// header called that "the ONE path v2 has not rebuilt yet"; this is that
// rebuild, and the `MachineDialog` import is gone from FleetDialogs.tsx with it.
//
// >>> THREE EDITABLE FIELDS. EVERYTHING ELSE IS SERVER-RESOLVED. <<<
// serialNumber (required, max 50) · productionYear (4 digits, 1900..now) ·
// locationId (optional). The validation rules are lifted field-for-field from
// M2.7's `CustomMachineTail.tsx:43-75`, which lifted them from v1 — the same
// three rules, so a serial that is valid on one add path is valid on both. The
// info note under the fields is the artboard's own explanation of why there is
// nothing else to fill in: the specs come from the catalog and are editable
// later in the machine profile.
//
// >>> RULE (d) / §9 D7: THE DIALOG STAYS OPEN UNTIL THE SERVER SAYS YES. <<<
// `onCreated` is passed straight into the hook and is called ONLY inside the
// success branch, after `await dispatch(createMachine(...)).unwrap()`. A
// failure sets `submitError`, touches no field, and renders `RetryNotice` whose
// onRetry re-submits the SAME values. See useCatalogAddForm.ts for the v1 line
// this fixes.
//
// The dialog shell is `TabDialog` (radius 12, glyph, `subtitle` back-link
// slot) — the v2 dialog shell, not a second one. `onBack` fills the subtitle
// slot only when the caller has somewhere to go back TO: the fleet add flow
// does (the picker), the catalog pages do not.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { integerRegex } from "../../../../shared/validators";
import { formatManufacturerWithHierarchy } from "../../../../shared/utils/machineUtils";
import { CatalogModelListItem } from "../../models/models";
import { BackLink } from "../add/addAtoms";
import { TextRow, TwoCol } from "../add/customMachineFieldAtoms";
import RetryNotice from "../feedback/RetryNotice";
import FormField from "../forms/FormField";
import FormSelect from "../forms/FormSelect";
import { CalendarGlyph, HashGlyph, ListGlyph, NoteGlyph } from "../forms/FormGlyphs";
import { locationOptions } from "../add/customMachineOptions";
import TabDialog from "../profile/tabs/TabDialog";
import { CatalogThumb, CategoryBadge, catalogThumbUrl } from "./catalogAtoms";
import useCatalogAddForm from "./useCatalogAddForm";

/** The footer button submits the body's form across the DOM boundary TabDialog
 *  puts between them — the same plain `form=` association M2.7 uses. */
const FORM_ID = "mv2-catalog-add-form";

export interface CatalogAddDialogV2Props {
  open: boolean;
  model: CatalogModelListItem;
  onClose: () => void;
  /** Fired ONLY after createMachine resolves — the sole close-on-success path. */
  onCreated: (id?: string) => void;
  /** "Späť na možnosti" / "Zmeniť stroj" — omitted when there is no way back. */
  onBack?: () => void;
}

const CatalogAddDialogV2: React.FC<CatalogAddDialogV2Props> = ({
  open,
  model,
  onClose,
  onCreated,
  onBack,
}) => {
  const { translate } = useTranslation(["myMachinery", "common"]);
  const f = useCatalogAddForm(open, model, onCreated);
  const currentYear = new Date().getFullYear();

  const manufacturerLine = model.manufacturer
    ? formatManufacturerWithHierarchy(
        model.manufacturer.name,
        model.manufacturer.parentManufacturerName
      )
    : "";

  const leave = (next: () => void) => {
    f.resetAll();
    next();
  };

  return (
    <TabDialog
      open={open}
      onOpenChange={(o) => {
        if (!o) leave(onClose);
      }}
      width={600}
      title={translate("myMachinery:v2.catalog.addFromCatalog")}
      glyph={<ListGlyph />}
      subtitle={
        onBack ? (
          <BackLink
            label={translate("myMachinery:v2.add.backToOptions")}
            onClick={() => leave(onBack)}
          />
        ) : undefined
      }
      footerNote={translate("myMachinery:v2.add.requiredNote")}
      footer={
        <React.Fragment>
          <button type="button" className="btn btn-ghost" onClick={() => leave(onClose)}>
            {translate("common:buttons.cancel")}
          </button>
          <button type="submit" form={FORM_ID} className="btn btn-mod" disabled={f.submitting}>
            <span aria-hidden style={{ fontSize: 15, lineHeight: 1, marginRight: 2 }}>
              +
            </span>
            {translate("myMachinery:v2.add.submit")}
          </button>
        </React.Fragment>
      }
    >
      <form id={FORM_ID} onSubmit={f.submit} noValidate style={{ minWidth: 0 }}>
        <div className="eyebrow" style={{ color: "var(--mod-accent)", margin: "2px 0 6px" }}>
          {translate("myMachinery:v2.catalog.selectedModel")}
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 11,
            padding: 10,
            marginBottom: 16,
            borderRadius: 9,
            background: "var(--mod-accent-soft)",
            border: "1px solid var(--mod-accent-line)",
            minWidth: 0,
          }}
        >
          <CatalogThumb
            url={catalogThumbUrl(model)}
            alt={model.modelName}
            width={62}
            height={44}
            radius={7}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              title={model.modelName}
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: "var(--ink)",
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {model.modelName}
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 7,
                marginTop: 3,
                minWidth: 0,
                flexWrap: "wrap",
              }}
            >
              {manufacturerLine && (
                <span
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 10,
                    color: "var(--muted)",
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {manufacturerLine}
                </span>
              )}
              <CategoryBadge code={model.categoryCode} />
            </div>
          </div>
          {onBack && (
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => leave(onBack)}
              style={{ height: 28, fontSize: 11.5, flexShrink: 0 }}
            >
              {translate("myMachinery:v2.catalog.changeModel")}
            </button>
          )}
        </div>

        <TwoCol>
          <TextRow
            form={f.form}
            name="serialNumber"
            label={translate("myMachinery:serialNumber")}
            glyph={<HashGlyph />}
            required
            mono
            maxLength={50}
            placeholder={translate("myMachinery:v2.add.serialPlaceholder")}
            rules={{ required: translate("common:forms.required") }}
          />
          <TextRow
            form={f.form}
            name="productionYear"
            label={translate("myMachinery:productionYear")}
            glyph={<CalendarGlyph />}
            mono
            maxLength={4}
            placeholder={String(currentYear)}
            rules={{
              pattern: {
                value: integerRegex,
                message: translate("myMachinery:fieldRequire4Digits"),
              },
              min: {
                value: 1900,
                message: translate("myMachinery:enterValidYearRange", { year: currentYear }),
              },
              max: {
                value: currentYear,
                message: translate("myMachinery:enterValidYearRange", { year: currentYear }),
              },
            }}
          />
        </TwoCol>

        <FormField label={translate("myMachinery:location")} htmlFor="mv2-catalog-add-location">
          <FormSelect
            id="mv2-catalog-add-location"
            placeholder={translate("myMachinery:v2.add.locationPlaceholder")}
            ariaLabel={translate("myMachinery:location")}
            value={f.locationId || undefined}
            options={locationOptions(f.locations, translate)}
            clearLabel={translate("myMachinery:v2.add.locationClear")}
            onChange={(value) => f.setLocationId(value || null)}
          />
        </FormField>

        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 8,
            padding: "9px 11px",
            borderRadius: 8,
            background: "var(--bg-soft)",
            border: "1px solid var(--border-soft)",
            fontSize: 11.5,
            color: "var(--muted)",
            minWidth: 0,
          }}
        >
          <span style={{ color: "var(--mod-accent)", flexShrink: 0, marginTop: 1 }}>
            <NoteGlyph />
          </span>
          <span style={{ minWidth: 0 }}>
            {translate("myMachinery:v2.catalog.specsInheritedNote")}
          </span>
        </div>

        {f.submitError && (
          <div style={{ marginTop: 12 }}>
            <RetryNotice
              title={f.submitError}
              hint={translate("myMachinery:v2.add.createFailedHint")}
              onRetry={f.submit}
              busy={f.submitting}
            />
          </div>
        )}
      </form>
    </TabDialog>
  );
};

export default CatalogAddDialogV2;
